import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { selectFilters, setTag } from '../store/slices/filtersSlice';
import { selectCurrentUser } from '../store/slices/authSlice';
import PinModal from '../components/PinModal';
import { Toast } from '../components/Toast';

function Home() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const filters = useSelector(selectFilters);
  const token = useSelector((state) => state.auth.token);
  const currentUser = useSelector(selectCurrentUser);
  const [pins, setPins] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPinId, setSelectedPinId] = useState(null);
  const [boards, setBoards] = useState([]);
  const [selectedBoardData, setSelectedBoardData] = useState(null);
  const containerRef = useRef(null);

  const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

  useEffect(() => {
    loadPins();
    loadBoards();
  }, []);

  useEffect(() => {
    // Load board data when filter changes
    if (filters.board) {
      const board = boards.find(b => b._id === filters.board);
      setSelectedBoardData(board);
    } else {
      setSelectedBoardData(null);
    }
  }, [filters.board, boards]);

  const loadBoards = async () => {
    try {
      const response = await fetch(API_BASE_URL + '/boards');
      if (response.ok) {
        const data = await response.json();
        setBoards(data);
      }
    } catch (error) {
      console.error('Error loading boards:', error);
    }
  };

  const loadPins = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(API_BASE_URL + '/pins');
      if (response.ok) {
        const data = await response.json();
        setPins(data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      Toast.error('Không thể tải dữ liệu');
    } finally {
      setIsLoading(false);
    }
  };

  const filterPins = () => {
    let filtered = pins;

    if (filters.board) {
      filtered = filtered.filter(pin => pin.board && pin.board._id === filters.board);
    }

    if (filters.search && filters.search.trim()) {
      const query = filters.search.toLowerCase();
      filtered = filtered.filter(pin =>
        pin.title?.toLowerCase().includes(query) ||
        pin.prompt?.toLowerCase().includes(query) ||
        pin.tags?.some(tag => tag.toLowerCase().includes(query))
      );
    }

    if (filters.tag) {
      filtered = filtered.filter(pin => 
        pin.tags && pin.tags.includes(filters.tag)
      );
    }

    if (filters.sort === 'newest') {
      filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    } else if (filters.sort === 'oldest') {
      filtered.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    } else if (filters.sort === 'popular') {
      filtered.sort((a, b) => (b.likes || 0) - (a.likes || 0));
    }

    return filtered;
  };

  const displayPins = filterPins();

  const getAspectRatioClass = (id) => {
    const aspectRatios = ['aspect-[3/4]', 'aspect-[2/3]', 'aspect-[4/5]', 'aspect-[1/1]', 'aspect-[5/4]'];
    let hash = 0;
    const str = String(id);
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return aspectRatios[Math.abs(hash) % aspectRatios.length];
  };

  if (isLoading) {
    return (
      <div className="px-3 sm:px-4 lg:px-6 py-4 bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 min-h-screen">
        {/* Skeleton Loading with better design */}
        <div className="columns-2 sm:columns-3 md:columns-4 lg:columns-5 xl:columns-6 gap-4">
          {[...Array(12)].map((_, i) => (
            <div key={i} className="mb-4 break-inside-avoid animate-pulse">
              <div className="bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-800 rounded-2xl shadow-lg" style={{ height: String(200 + Math.random() * 200) + 'px' }}></div>
              <div className="mt-3 space-y-2 px-1">
                <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-800 rounded-full w-3/4"></div>
                <div className="h-3 bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-800 rounded-full w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full overflow-y-auto px-3 sm:px-4 lg:px-6 py-4 bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800" ref={containerRef}>
      {/* Board Tags Section - Always show when board is selected */}
      {selectedBoardData && selectedBoardData.tags && selectedBoardData.tags.length > 0 && (
        <div className="mb-6 animate-fade-in-up">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
                  <span className="material-symbols-rounded text-white text-[20px]">
                    {selectedBoardData.icon || 'folder'}
                  </span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                    Tags trong "{selectedBoardData.name}"
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {filters.tag ? `Đang lọc: #${filters.tag}` : 'Chọn tag để lọc prompts'}
                  </p>
                </div>
              </div>
              {filters.tag && (
                <button
                  onClick={() => dispatch(setTag(''))}
                  className="px-4 py-2 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full text-sm font-medium hover:bg-red-200 dark:hover:bg-red-900/50 transition-all flex items-center gap-2"
                >
                  <span className="material-symbols-rounded text-[18px]">close</span>
                  Xóa lọc
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {selectedBoardData.tags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => {
                    // Toggle tag filter
                    if (filters.tag === tag) {
                      dispatch(setTag('')); // Clear filter if clicking same tag
                    } else {
                      dispatch(setTag(tag)); // Set new tag filter
                    }
                  }}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all hover:scale-105 ${
                    filters.tag === tag
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  #{tag}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {displayPins.length === 0 ? (
        <div className="flex items-center justify-center min-h-[40vh]">
          <div className="text-center animate-fade-in-up">
            <div className="relative w-32 h-32 mx-auto mb-6">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/20 dark:to-purple-900/20 rounded-full animate-pulse"></div>
              <span className="absolute inset-0 flex items-center justify-center material-symbols-rounded text-6xl text-gray-400 dark:text-gray-600">
                search_off
              </span>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
              Không tìm thấy kết quả
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">
              {filters.tag 
                ? `Không có prompts nào với tag "#${filters.tag}". Thử chọn tag khác ở trên.`
                : 'Thử tìm kiếm bằng từ khóa khác hoặc xóa bộ lọc để xem tất cả prompts'
              }
            </p>
            {!filters.tag && (
              <button
                onClick={() => window.location.reload()}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-full font-semibold shadow-lg hover:shadow-xl transition-all hover:scale-105"
              >
                Xem tất cả prompts
              </button>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="columns-2 sm:columns-3 md:columns-4 lg:columns-5 xl:columns-6 gap-4">
            {displayPins.map((pin, index) => (
              <div
                key={pin._id}
                className="mb-4 break-inside-avoid group cursor-pointer animate-fade-in-up"
                style={{ animationDelay: String(Math.min(index * 0.05, 1)) + 's' }}
                onClick={() => setSelectedPinId(pin._id)}
              >
                <div className={`relative rounded-2xl overflow-hidden bg-white dark:bg-gray-800 shadow-md hover:shadow-2xl transition-all duration-500 group-hover:-translate-y-2 group-hover:scale-[1.02] ${getAspectRatioClass(pin._id)}`}>
                  {/* Image with gradient overlay */}
                  <img
                    src={pin.image}
                    alt={pin.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                  />
                  
                  {/* Gradient overlay on hover */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    
                    {/* Bottom info with slide up animation */}
                    <div className="absolute bottom-0 left-0 right-0 p-4 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                      <div className="flex items-center justify-between text-white mb-2">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-1 bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full hover:bg-white/30 transition-colors">
                            <span className="material-symbols-rounded text-[16px]">favorite</span>
                            <span className="font-bold text-sm">{pin.likes || 0}</span>
                          </div>
                          <div className="flex items-center gap-1 bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full hover:bg-white/30 transition-colors">
                            <span className="material-symbols-rounded text-[16px]">visibility</span>
                            <span className="font-bold text-sm">{pin.views || 0}</span>
                          </div>
                        </div>
                        
                        {/* Quick action buttons */}
                        <div className="flex items-center gap-2">
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (!token || !currentUser) {
                                Toast.error('Vui lòng đăng nhập để sao chép prompt!');
                                navigate('/login');
                                return;
                              }
                              try {
                                await navigator.clipboard.writeText(pin.prompt);
                                Toast.success('Đã sao chép prompt!');
                                // Track copy
                                fetch(`${import.meta.env.VITE_API_URL}/pins/${pin._id}/copy`, {
                                  method: 'POST',
                                  headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${token}`
                                  }
                                }).catch(err => console.error('Error tracking copy:', err));
                              } catch (error) {
                                Toast.error('Không thể sao chép!');
                              }
                            }}
                            className="w-8 h-8 bg-white/20 backdrop-blur-sm hover:bg-white/30 rounded-full flex items-center justify-center transition-all hover:scale-110"
                          >
                            <span className="material-symbols-rounded text-[18px]">content_copy</span>
                          </button>
                       
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Card info below image */}
                <div className="mt-3 px-1">
                  <h3 className="font-bold text-sm text-gray-900 dark:text-white line-clamp-2 mb-1.5 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                    {pin.title}
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2 mb-2">
                    {pin.prompt}
                  </p>
                  
                  {/* User info - clickable */}
                  {pin.user && (
                    <div 
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/user/${pin.user}`);
                      }}
                      className="flex items-center gap-2 mb-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-1.5 rounded-lg transition-colors"
                    >
                      <img
                        src={`https://api.dicebear.com/7.x/notionists/svg?seed=${pin.user}&backgroundColor=e9e9e9`}
                        alt={pin.user}
                        className="w-6 h-6 rounded-full bg-gray-100"
                      />
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                        {pin.user}
                      </span>
                    </div>
                  )}
                  
                  {pin.board && (
                    <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full w-fit">
                      <span className="material-symbols-rounded text-[14px]">
                        {pin.board.icon || 'folder'}
                      </span>
                      <span className="truncate font-medium">{pin.board.name}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Pin Modal */}
      {selectedPinId && (
        <PinModal pinId={selectedPinId} onClose={() => setSelectedPinId(null)} />
      )}
    </div>
  );
}

export default Home;
